import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/cjs/Page/AnnotationLayer.css';
import 'react-pdf/dist/cjs/Page/TextLayer.css';
import { apiFetch } from '../utils/apiClient';
import { getBackendBaseOrigin } from '../utils/network';

// PDF.js worker - usa versione legacy compatibile
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.legacy.js';

interface EventDocument {
    id: string;
    name: string;
    description?: string;
    docType: string;
    url: string;
    createdAt: string;
    updatedAt: string;
}

interface EventsProps {
    open: boolean;
    onClose: () => void;
}

const Events: React.FC<EventsProps> = ({ open, onClose }) => {
    const { tenantId } = useTenant();
    const { t, i18n } = useTranslation();
    const [events, setEvents] = useState<EventDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<EventDocument | null>(null);
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const lastFetchedAtRef = useRef<number>(0);
    const activeControllerRef = useRef<AbortController | null>(null);
    const pdfContainerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    const dateLocale = useMemo(() => {
        const lang = i18n.language || 'it';
        if (lang.includes('-')) {
            return lang;
        }
        const localeMap: Record<string, string> = {
            it: 'it-IT',
            en: 'en-US',
            de: 'de-DE'
        };
        return localeMap[lang] || 'it-IT';
    }, [i18n.language]);

    const loadEvents = useCallback(async ({ forceRefresh = false, showLoader = false } = {}) => {
        if (!tenantId) return;

        const now = Date.now();
        const cacheIsFresh = !forceRefresh && lastFetchedAtRef.current && (now - lastFetchedAtRef.current) < 120000;
        if (cacheIsFresh) {
            if (showLoader) setLoading(false);
            return;
        }

        activeControllerRef.current?.abort();
        const controller = new AbortController();
        activeControllerRef.current = controller;

        if (showLoader) setLoading(true);
        setErrorKey(null);

        try {
            const response = await apiFetch(`/api/public/tenants/${tenantId}/documents`, {
                method: 'GET',
                signal: controller.signal
            });

            if (response.ok) {
                const text = await response.text();
                let documents = [];
                try {
                    documents = text ? JSON.parse(text) : [];
                } catch (parseError) {
                    console.error('Errore parsing JSON:', parseError, 'Response text:', text);
                    documents = [];
                }
                // Filtra solo i documenti di tipo EVENTO e prepara gli URL completi
                const eventDocs = documents.filter((doc: any) => doc.docType === 'EVENTO').map((doc: any) => ({
                    ...doc,
                    url: doc.url?.startsWith('/') ? `${getBackendBaseOrigin()}${doc.url}` : doc.url
                }));
                setEvents(eventDocs);
                lastFetchedAtRef.current = Date.now();
            } else {
                const errorText = await response.text();
                console.error('Errore response:', response.status, errorText);
                setErrorKey('errorLoading');
            }
        } catch (err: any) {
            if (controller.signal.aborted) {
                return;
            }
            console.error('Errore caricamento eventi:', err);
            setErrorKey('connectionError');
        } finally {
            if (showLoader) {
                setLoading(false);
            }
            if (activeControllerRef.current === controller) {
                activeControllerRef.current = null;
            }
        }
    }, [tenantId]);

    useEffect(() => {
        if (tenantId) {
            loadEvents({ forceRefresh: true, showLoader: false });
        }
    }, [tenantId, loadEvents]);

    useEffect(() => {
        if (open && tenantId) {
            loadEvents({ showLoader: true });
        } else if (!open) {
            setSelectedEvent(null);
            setPageNumber(1);
            setNumPages(null);
            setErrorKey(null);
        }
    }, [open, tenantId, loadEvents]);

    useEffect(() => () => {
        activeControllerRef.current?.abort();
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            const stillExists = events.some(event => event.id === selectedEvent.id);
            if (!stillExists) {
                setSelectedEvent(null);
            }
        }
    }, [events, selectedEvent]);

    useEffect(() => {
        if (!open) return;
        if (typeof window === 'undefined') return;

        const node = pdfContainerRef.current;
        if (!node) return;

        const updateWidth = () => {
            setContainerWidth(node.clientWidth || 0);
        };

        updateWidth();

        let resizeObserver: ResizeObserver | null = null;
        const globalWindow = window as Window & typeof globalThis;
        const supportsResizeObserver = typeof ResizeObserver !== 'undefined';

        if (supportsResizeObserver) {
            resizeObserver = new ResizeObserver(() => updateWidth());
            resizeObserver.observe(node);
        } else {
            globalWindow.addEventListener('resize', updateWidth);
        }

        globalWindow.addEventListener('orientationchange', updateWidth);

        return () => {
            resizeObserver?.disconnect();
            if (!supportsResizeObserver) {
                globalWindow.removeEventListener('resize', updateWidth);
            }
            globalWindow.removeEventListener('orientationchange', updateWidth);
        };
    }, [open]);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    const onDocumentLoadError = (error: Error) => {
        console.error('Errore caricamento PDF:', error);
        setErrorKey('documentLoadError');
    };

    const handleEventSelect = (event: EventDocument) => {
        setSelectedEvent(event);
        setErrorKey(null);
    };

    const handleBack = () => {
        setSelectedEvent(null);
        setPageNumber(1);
        setNumPages(null);
        setErrorKey(null);
    };

    const changePage = (offset: number) => {
        setPageNumber(prevPageNumber => prevPageNumber + offset);
    };

    const previousPage = () => changePage(-1);
    const nextPage = () => changePage(1);

    const sortedEvents = useMemo(() => {
        return [...events].sort((a, b) => {
            const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime() || 0;
            const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime() || 0;
            return bDate - aDate;
        });
    }, [events]);

    if (!open) return null;

    return (
        <div className="events-overlay">
            <div className="events-content">
                <div className="events-header">
                    <h2>{t('events.title')}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {loading && (
                    <div className="loading">
                        <p>{t('events.loading')}</p>
                    </div>
                )}

                {errorKey && (
                    <div className="error-message">
                        <p>{t(`events.${errorKey}`)}</p>
                    </div>
                )}

                {!selectedEvent ? (
                    // Lista eventi
                    <div className="events-list">
                        {events.length === 0 && !loading ? (
                            <div className="no-events">
                                <p>{t('events.noEvents')}</p>
                                <p className="help-text">{t('events.noEventsHelp')}</p>
                            </div>
                        ) : (
                            <div className="events-grid">
                                {sortedEvents.map((event) => (
                                    <div
                                        key={event.id}
                                        className="event-card"
                                        onClick={() => handleEventSelect(event)}
                                    >
                                        <div className="event-card-content">
                                            <h3>{event.name}</h3>
                                            {event.description && (
                                                <p className="event-description">{event.description}</p>
                                            )}
                                            <div className="event-meta">
                                                <span className="event-date">
                                                    {new Date(event.createdAt).toLocaleDateString(dateLocale)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="event-card-action">
                                            <span>{t('events.viewAction')}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    // Visualizzazione documento PDF
                    <div className="event-viewer">
                        <div className="viewer-header">
                            <button className="back-btn" onClick={handleBack}>
                                {t('events.backToList')}
                            </button>
                            <h3>{selectedEvent.name}</h3>
                        </div>

                        <div className="pdf-container" ref={pdfContainerRef}>
                            {selectedEvent.url ? (
                                <>
                                    <Document
                                        file={selectedEvent.url}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadError={onDocumentLoadError}
                                        loading={<div className="pdf-loading">{t('events.documentLoading')}</div>}
                                    >
                                        <Page
                                            pageNumber={pageNumber}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            width={containerWidth ? Math.min(Math.max(containerWidth - 32, 240), 900) : undefined}
                                            loading={<div className="page-loading">{t('events.pageLoading')}</div>}
                                        />
                                    </Document>

                                    {numPages && numPages > 1 && (
                                        <div className="pdf-controls">
                                            <button
                                                disabled={pageNumber <= 1}
                                                onClick={previousPage}
                                                className="page-btn"
                                            >
                                                {t('events.previous')}
                                            </button>
                                            <span className="page-info">
                                                {t('events.pageInfo', { current: pageNumber, total: numPages })}
                                            </span>
                                            <button
                                                disabled={pageNumber >= numPages}
                                                onClick={nextPage}
                                                className="page-btn"
                                            >
                                                {t('events.next')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="no-document">
                                    <p>{t('events.documentUnavailable')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Events;